import { NavLink } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Avatar } from '../components/ui/Avatar';
import { Badge } from '../components/ui/Badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/Card';
import { EmptyState } from '../components/ui/EmptyState';
import { icons } from '../components/ui/Icons';
import { cn } from '../lib/utils';

interface StatusItem {
  label: string;
  description: string;
  icon: keyof typeof icons;
  href: string;
}

const statusItems: StatusItem[] = [
  {
    label: 'Aktive Meldungen',
    description: 'Neuigkeiten aus dem Intranet',
    icon: 'Bell',
    href: '/kommunikation/news',
  },
  {
    label: 'Offene Aufgaben',
    description: 'Ihre aktuellen Arbeitsaufgaben',
    icon: 'ClipboardList',
    href: '/organisation/termine',
  },
  {
    label: 'Termine heute',
    description: 'Einträge aus Ihrem Kalender',
    icon: 'Calendar',
    href: '/organisation/kalender',
  },
  {
    label: 'Systemstatus',
    description: 'Verfügbarkeit der Intranet-Dienste',
    icon: 'Activity',
    href: '/system/status',
  },
];

const actionLinkClasses =
  'inline-flex items-center gap-2 rounded-md bg-primary-600 px-3 py-2 text-sm font-medium text-white shadow-subtle transition-colors hover:bg-primary-700 hover:shadow-card focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800';

const navigationLinkClasses =
  'inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-primary-50 hover:text-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:hover:bg-gray-700 dark:hover:text-gray-200';

function getGreeting() {
  const hour = new Date().getHours();

  if (hour < 5) return 'Guten Abend';
  if (hour < 12) return 'Guten Morgen';
  if (hour < 18) return 'Guten Tag';
  return 'Guten Abend';
}

export function HomePage() {
  const { user } = useAuth();
  const userName = user?.discordDisplayName?.trim() || user?.discordUsername?.trim() || 'Mitarbeiter';
  const formattedDate = new Intl.DateTimeFormat('de-DE', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  }).format(new Date());

  return (
    <div className="mx-auto max-w-6xl space-y-8">
      <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-center gap-4">
          <Avatar src={user?.discordAvatar} name={userName} size="lg" />
          <div className="min-w-0">
            <p className="text-sm font-medium text-primary-700 dark:text-primary-300">{getGreeting()}</p>
            <h1 className="truncate text-2xl font-semibold text-gray-900 dark:text-white">{userName}</h1>
            <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">{formattedDate}</p>
          </div>
        </div>
        <Badge variant="medical" size="sm" className="w-fit">Klinikum Ravenbruch Intranet</Badge>
      </div>

      <section aria-labelledby="status-overview-title">
        <div className="mb-4">
          <h2 id="status-overview-title" className="text-lg font-semibold text-gray-900 dark:text-white">
            Statusübersicht
          </h2>
          <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
            Ein kompakter Einstieg in Ihre aktuellen Bereiche.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {statusItems.map(item => {
            const Icon = icons[item.icon];

            return (
              <Card key={item.label} variant="hover" padding="md" className="h-full border-t-2 border-t-primary-500">
                <CardContent className="flex min-w-0 items-center gap-3">
                  <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md bg-primary-50 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400">
                    <Icon className="h-4 w-4" aria-hidden="true" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{item.label}</p>
                    <Badge variant="gray" size="sm" className="mt-2">Noch nicht geladen</Badge>
                  </div>
                  <NavLink to={item.href} className={cn(navigationLinkClasses, 'ml-auto')} aria-label={`${item.label} öffnen`}>
                    <icons.ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </NavLink>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      <section aria-label="Aktuelle Informationen" className="space-y-6">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <Card className="h-full">
            <CardHeader>
              <CardTitle>Aktuelle Meldungen</CardTitle>
              <CardDescription>Neuigkeiten und organisationale Informationen.</CardDescription>
            </CardHeader>
            <CardContent>
              <EmptyState
                icon="Bell"
                title="Noch keine Meldungen geladen"
                description="Aktuelle Meldungen erscheinen hier, sobald sie im Intranet bereitstehen."
                action={
                  <NavLink to="/kommunikation/news" className={actionLinkClasses}>
                    <icons.FileText className="h-4 w-4" aria-hidden="true" />
                    News öffnen
                  </NavLink>
                }
                className="min-h-[180px] py-8"
              />
            </CardContent>
          </Card>

          <Card className="h-full">
            <CardHeader>
              <CardTitle>Heutige Termine</CardTitle>
              <CardDescription>Einträge aus Ihrem persönlichen Kalender.</CardDescription>
            </CardHeader>
            <CardContent>
              <EmptyState
                icon="Calendar"
                title="Keine Termine für heute"
                description="Ihre heutigen Termine werden hier übersichtlich angezeigt."
                action={
                  <NavLink to="/organisation/kalender" className={actionLinkClasses}>
                    <icons.Calendar className="h-4 w-4" aria-hidden="true" />
                    Kalender öffnen
                  </NavLink>
                }
                className="min-h-[180px] py-8"
              />
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Wichtige Hinweise</CardTitle>
            <CardDescription>Zentrale Informationen für den Arbeitsalltag.</CardDescription>
          </CardHeader>
          <CardContent>
            <EmptyState
              icon="Info"
              title="Keine Hinweise verfügbar"
              description="Bekanntmachungen und wichtige Hinweise werden hier gesammelt."
              action={
                <NavLink to="/kommunikation/bekanntmachungen" className={actionLinkClasses}>
                  <icons.Bell className="h-4 w-4" aria-hidden="true" />
                  Bekanntmachungen öffnen
                </NavLink>
              }
              className="min-h-[180px] py-8"
            />
          </CardContent>
        </Card>
      </section>
    </div>
  );
}
